import React, { useState, useEffect } from 'react';
import { candidateApi, interviewApi } from '../services/api';
import { Candidate, InterviewSession } from '../types/models';

const ApiTest: React.FC = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [interviews, setInterviews] = useState<InterviewSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 候補者一覧を取得
  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await candidateApi.getCandidates();
      if (response.success && response.data) {
        setCandidates(response.data);
        setMessage('✅ 候補者一覧の取得に成功しました');
      } else {
        setMessage(`❌ 候補者一覧の取得に失敗: ${response.message || response.error}`);
      }
    } catch (error) {
      setMessage(`❌ エラー: ${error}`);
    }
    setLoading(false);
  };

  // 面接一覧を取得
  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await interviewApi.getInterviews();
      if (response.success && response.data) {
        setInterviews(response.data);
        setMessage('✅ 面接一覧の取得に成功しました');
      } else {
        setMessage(`❌ 面接一覧の取得に失敗: ${response.message || response.error}`);
      }
    } catch (error) {
      setMessage(`❌ エラー: ${error}`);
    }
    setLoading(false);
  };

  // 新しい候補者を作成
  const createTestCandidate = async () => {
    setLoading(true);
    try {
      const testData = {
        name: 'テスト 花子',
        email: 'test@example.com',
        phone: '090-0000-0000',
        position: 'テストエンジニア',
      };

      const response = await candidateApi.createCandidate(testData);
      if (response.success) {
        setMessage('✅ テスト候補者の作成に成功しました');
        fetchCandidates(); // 一覧を再取得
      } else {
        setMessage(`❌ テスト候補者の作成に失敗: ${response.message || response.error}`);
      }
    } catch (error) {
      setMessage(`❌ エラー: ${error}`);
    }
    setLoading(false);
  };

  // コンポーネントマウント時に初回データ取得
  useEffect(() => {
    fetchCandidates();
    fetchInterviews();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-green-600">API接続テスト</h1>

      {message && <div className="mb-4 p-3 rounded border bg-gray-50">{message}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 候補者管理テスト */}
        <div className="bg-white p-4 rounded-lg shadow border">
          <h2 className="text-lg font-semibold mb-4 text-green-600">候補者管理API</h2>

          <div className="space-y-2 mb-4">
            <button
              onClick={fetchCandidates}
              disabled={loading}
              className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
            >
              {loading ? '読み込み中...' : '候補者一覧取得'}
            </button>

            <button
              onClick={createTestCandidate}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
            >
              {loading ? '作成中...' : 'テスト候補者作成'}
            </button>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">候補者一覧 ({candidates.length}件)</h3>
            <div className="max-h-32 overflow-y-auto text-sm">
              {candidates.map((candidate) => (
                <div key={candidate.id} className="p-2 border rounded">
                  <div className="font-medium">{candidate.name}</div>
                  <div className="text-gray-600">{candidate.email}</div>
                  <div className="text-gray-500">{candidate.position}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 面接管理テスト */}
        <div className="bg-white p-4 rounded-lg shadow border">
          <h2 className="text-lg font-semibold mb-4 text-green-600">面接管理API</h2>

          <div className="space-y-2 mb-4">
            <button
              onClick={fetchInterviews}
              disabled={loading}
              className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
            >
              {loading ? '読み込み中...' : '面接一覧取得'}
            </button>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">面接一覧 ({interviews.length}件)</h3>
            <div className="max-h-32 overflow-y-auto text-sm">
              {interviews.map((interview) => (
                <div key={interview.id} className="p-2 border rounded">
                  <div className="font-medium">{interview.candidateName}</div>
                  <div className="text-gray-600">
                    {interview.date} {interview.time}
                  </div>
                  <div className="text-gray-500">{interview.position}</div>
                  <div className="text-xs text-blue-600">{interview.status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTest;
